import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  Plus,
  Download,
  Filter,
  Edit2,
  Save,
  X,
  Eye,
  EyeOff,
  Settings,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface AccountRow {
  id: string;
  row_number: number;
  account_number: string;
  property_code: string;
  property_name: string;
  unit_number: string;
  commodity_type: string;
  vendor_name: string;
  vendor_id?: string;
  status: 'complete' | 'missing_data' | 'error';
  errors?: string[];
  warnings?: string[];
  is_common_area?: boolean;
}

interface EditableAccountTableProps {
  accounts: AccountRow[];
  properties: Array<{ code: string; name: string }>;
  vendors: Array<{ id: string; name: string }>;
  commodities: string[];
  onCellEdit?: (rowId: string, field: string, value: any) => void;
  onBulkEdit?: (selectedIds: string[], updates: Record<string, any>) => void;
  onAddAccount?: () => void;
  onExport?: () => void;
  onRowClick?: (row: AccountRow) => void;
  showConfiguration?: boolean;
}

export default function EditableAccountTable({
  accounts,
  properties,
  vendors,
  commodities,
  onCellEdit,
  onBulkEdit,
  onAddAccount,
  onExport,
  onRowClick,
  showConfiguration = true,
}: EditableAccountTableProps) {
  const [editingCell, setEditingCell] = useState<{ rowId: string; field: string } | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterCommodity, setFilterCommodity] = useState<string>('all');
  const [filterProperty, setFilterProperty] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'excel' | 'card'>('excel');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  
  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set([
    'account_number',
    'property_name',
    'unit_number',
    'commodity_type',
    'vendor_name',
    'status',
  ]));
  
  const allColumns = [
    { key: 'account_number', label: 'Account Number' },
    { key: 'property_code', label: 'Property Code' },
    { key: 'property_name', label: 'Property Name' },
    { key: 'unit_number', label: 'Unit Number' },
    { key: 'commodity_type', label: 'Commodity Type' },
    { key: 'vendor_name', label: 'Vendor Name' },
    { key: 'status', label: 'Status' },
    { key: 'errors', label: 'Errors' },
    { key: 'warnings', label: 'Warnings' },
  ];

  const filteredAccounts = useMemo(() => {
    return accounts.filter((account) => {
      if (filterStatus !== 'all') {
        if (filterStatus === 'complete' && account.status !== 'complete') return false;
        if (filterStatus === 'missing' && account.status !== 'missing_data') return false;
        if (filterStatus === 'error' && account.status !== 'error') return false;
      }
      if (filterCommodity !== 'all' && account.commodity_type !== filterCommodity) return false;
      if (filterProperty !== 'all' && account.property_code !== filterProperty) return false;
      return true;
    });
  }, [accounts, filterStatus, filterCommodity, filterProperty]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'complete':
        return <Badge className="bg-green-50 text-green-700 border-green-200">✓ Complete</Badge>;
      case 'missing_data':
        return <Badge className="bg-amber-50 text-amber-700 border-amber-200">⚠ Missing Data</Badge>;
      case 'error':
        return <Badge variant="destructive">❌ Error</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleCellDoubleClick = (rowId: string, field: string) => {
    setEditingCell({ rowId, field });
  };

  const handleCellSave = (rowId: string, field: string, value: any) => {
    onCellEdit?.(rowId, field, value);
    setEditingCell(null);
  };

  const handleSelectAll = () => {
    if (selectedRows.size === filteredAccounts.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(filteredAccounts.map(a => a.id)));
    }
  };

  const handleRowSelect = (rowId: string) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(rowId)) {
      newSelected.delete(rowId);
    } else {
      newSelected.add(rowId);
    }
    setSelectedRows(newSelected);
  };

  const statusCounts = useMemo(() => {
    return {
      all: accounts.length,
      complete: accounts.filter(a => a.status === 'complete').length,
      missing: accounts.filter(a => a.status === 'missing_data').length,
      error: accounts.filter(a => a.status === 'error').length,
    };
  }, [accounts]);

  // Summary view removed - using only excel and card views

  return (
    <div className="space-y-4">
      {/* Actions Bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-2">
              {onAddAccount && (
                <Button size="sm" onClick={onAddAccount}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Missing Accounts
                </Button>
              )}
              <Button variant="outline" size="sm">
                Import More
              </Button>
              {onExport && (
                <Button variant="outline" size="sm" onClick={() => {
                  // Export to CSV
                  const headers = ['Account Number', 'Property', 'Unit', 'Commodity', 'Vendor', 'Status'];
                  const rows = accounts.map(acc => [
                    acc.account_number || '',
                    acc.property_name || acc.property_code || '',
                    acc.unit_number || '',
                    acc.commodity_type || '',
                    acc.vendor_name || '',
                    acc.status,
                  ]);
                  
                  const csv = [
                    headers.join(','),
                    ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
                  ].join('\n');
                  
                  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `accounts_export_${new Date().toISOString().split('T')[0]}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  window.URL.revokeObjectURL(url);
                  
                  if (onExport) onExport();
                }}>
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Filters:</span>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status ({statusCounts.all})</SelectItem>
                  <SelectItem value="complete">✓ Complete ({statusCounts.complete})</SelectItem>
                  <SelectItem value="missing">⚠ Missing ({statusCounts.missing})</SelectItem>
                  <SelectItem value="error">❌ Errors ({statusCounts.error})</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterCommodity} onValueChange={setFilterCommodity}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Commodity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Commodities</SelectItem>
                  {commodities.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterProperty} onValueChange={setFilterProperty}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Property" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Properties</SelectItem>
                  {properties.map((p) => (
                    <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">View:</span>
              <Button
                variant={viewMode === 'excel' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('excel')}
              >
                ⊞ Excel View
              </Button>
              <Button
                variant={viewMode === 'card' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('card')}
              >
                ○ Card View
              </Button>
              <Button
                variant={viewMode === 'card' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('card')}
              >
                ○ Card View
              </Button>
              
              {/* Column Visibility Toggle */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    <Settings className="h-4 w-4" />
                    Columns
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Toggle Columns</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {allColumns.map((column) => (
                    <DropdownMenuCheckboxItem
                      key={column.key}
                      checked={visibleColumns.has(column.key)}
                      onCheckedChange={(checked) => {
                        const newVisible = new Set(visibleColumns);
                        if (checked) {
                          newVisible.add(column.key);
                        } else {
                          newVisible.delete(column.key);
                        }
                        setVisibleColumns(newVisible);
                      }}
                    >
                      {column.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Excel-Style Table */}
      {viewMode === 'excel' && (
        <Card>
          <CardHeader>
            <CardTitle>Utility Accounts - Complete Data Review</CardTitle>
            <CardDescription>
              Showing {filteredAccounts.length} of {accounts.length} accounts
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <input
                        type="checkbox"
                        checked={selectedRows.size === filteredAccounts.length && filteredAccounts.length > 0}
                        onChange={handleSelectAll}
                        className="cursor-pointer"
                      />
                    </TableHead>
                    {visibleColumns.has('account_number') && <TableHead>Account #</TableHead>}
                    {visibleColumns.has('property_code') && <TableHead>Property Code</TableHead>}
                    {visibleColumns.has('property_name') && <TableHead>Property</TableHead>}
                    {visibleColumns.has('unit_number') && <TableHead>Unit</TableHead>}
                    {visibleColumns.has('commodity_type') && <TableHead>Commodity</TableHead>}
                    {visibleColumns.has('vendor_name') && <TableHead>Vendor</TableHead>}
                    {visibleColumns.has('status') && <TableHead>Status</TableHead>}
                    {visibleColumns.has('errors') && <TableHead>Errors</TableHead>}
                    {visibleColumns.has('warnings') && <TableHead>Warnings</TableHead>}
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAccounts.map((account) => {
                    const isEditing = editingCell?.rowId === account.id;
                    const isSelected = selectedRows.has(account.id);

                    return (
                      <TableRow
                        key={account.id}
                        className={isSelected ? 'bg-primary/5' : ''}
                        onClick={() => onRowClick?.(account)}
                      >
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleRowSelect(account.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="cursor-pointer"
                          />
                        </TableCell>
                        {visibleColumns.has('account_number') && (
                          <TableCell
                            onDoubleClick={() => handleCellDoubleClick(account.id, 'account_number')}
                            className="cursor-pointer"
                          >
                            {isEditing && editingCell?.field === 'account_number' ? (
                              <Input
                                defaultValue={account.account_number}
                                onBlur={(e) => handleCellSave(account.id, 'account_number', e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleCellSave(account.id, 'account_number', e.currentTarget.value);
                                  } else if (e.key === 'Escape') {
                                    setEditingCell(null);
                                  }
                                }}
                                autoFocus
                                className="h-8"
                              />
                            ) : (
                              <span className={!account.account_number ? 'text-red-600 italic' : ''}>
                                {account.account_number || '[Required]'}
                              </span>
                            )}
                          </TableCell>
                        )}
                        {visibleColumns.has('property_code') && (
                          <TableCell className="text-xs text-muted-foreground">
                            {account.property_code}
                          </TableCell>
                        )}
                        {visibleColumns.has('property_name') && (
                          <TableCell
                            onDoubleClick={() => handleCellDoubleClick(account.id, 'property_code')}
                            className="cursor-pointer"
                          >
                            {isEditing && editingCell?.field === 'property_code' ? (
                              <Select
                                defaultValue={account.property_code}
                                onValueChange={(value) => handleCellSave(account.id, 'property_code', value)}
                              >
                                <SelectTrigger className="w-[200px]">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {properties.map((p) => (
                                    <SelectItem key={p.code} value={p.code}>
                                      {p.code} - {p.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <div>
                                <div className="font-medium">{account.property_code}</div>
                                <div className="text-xs text-muted-foreground">{account.property_name}</div>
                              </div>
                            )}
                          </TableCell>
                        )}
                        {visibleColumns.has('unit_number') && (
                          <TableCell
                            onDoubleClick={() => handleCellDoubleClick(account.id, 'unit_number')}
                            className="cursor-pointer"
                          >
                            {isEditing && editingCell?.field === 'unit_number' ? (
                              <Input
                                defaultValue={account.unit_number}
                                onBlur={(e) => handleCellSave(account.id, 'unit_number', e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleCellSave(account.id, 'unit_number', e.currentTarget.value);
                                  } else if (e.key === 'Escape') {
                                    setEditingCell(null);
                                  }
                                }}
                                autoFocus
                                className="h-8"
                              />
                            ) : (
                              <span>
                                {account.unit_number || (account.is_common_area ? 'Common Area' : '-')}
                              </span>
                            )}
                          </TableCell>
                        )}
                        {visibleColumns.has('commodity_type') && (
                          <TableCell
                            onDoubleClick={() => handleCellDoubleClick(account.id, 'commodity_type')}
                            className="cursor-pointer"
                          >
                            {isEditing && editingCell?.field === 'commodity_type' ? (
                              <Select
                                defaultValue={account.commodity_type}
                                onValueChange={(value) => handleCellSave(account.id, 'commodity_type', value)}
                              >
                                <SelectTrigger className="w-[150px]">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {commodities.map((c) => (
                                    <SelectItem key={c} value={c}>{c}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              account.commodity_type
                            )}
                          </TableCell>
                        )}
                        {visibleColumns.has('vendor_name') && (
                          <TableCell
                            onDoubleClick={() => handleCellDoubleClick(account.id, 'vendor_name')}
                            className="cursor-pointer"
                          >
                            {isEditing && editingCell?.field === 'vendor_name' ? (
                              <Select
                                defaultValue={account.vendor_id || account.vendor_name}
                                onValueChange={(value) => {
                                  const vendor = vendors.find(v => v.id === value || v.name === value);
                                  handleCellSave(account.id, 'vendor_id', vendor?.id);
                                  handleCellSave(account.id, 'vendor_name', vendor?.name);
                                }}
                              >
                                <SelectTrigger className="w-[200px]">
                                  <SelectValue placeholder="Select Vendor" />
                                </SelectTrigger>
                                <SelectContent>
                                  {vendors.map((v) => (
                                    <SelectItem key={v.id} value={v.id}>
                                      {v.name}
                                    </SelectItem>
                                  ))}
                                  <SelectItem value="add_new">+ Add New Vendor</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className={!account.vendor_name ? 'text-red-600 italic' : ''}>
                                {account.vendor_name || '[Required]'}
                              </span>
                            )}
                          </TableCell>
                        )}
                        {visibleColumns.has('status') && (
                          <TableCell>
                            {getStatusBadge(account.status)}
                          </TableCell>
                        )}
                        {visibleColumns.has('errors') && (
                          <TableCell>
                            {account.errors && account.errors.length > 0 ? (
                              <div className="flex flex-col gap-1">
                                {account.errors.slice(0, 2).map((error, idx) => (
                                  <Badge key={idx} variant="destructive" className="text-xs">
                                    {error}
                                  </Badge>
                                ))}
                                {account.errors.length > 2 && (
                                  <span className="text-xs text-muted-foreground">
                                    +{account.errors.length - 2} more
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-xs">-</span>
                            )}
                          </TableCell>
                        )}
                        {visibleColumns.has('warnings') && (
                          <TableCell>
                            {account.warnings && account.warnings.length > 0 ? (
                              <div className="flex flex-col gap-1">
                                {account.warnings.slice(0, 2).map((warning, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs border-amber-200 text-amber-700">
                                    {warning}
                                  </Badge>
                                ))}
                                {account.warnings.length > 2 && (
                                  <span className="text-xs text-muted-foreground">
                                    +{account.warnings.length - 2} more
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-xs">-</span>
                            )}
                          </TableCell>
                        )}
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {showConfiguration && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedRow(account.id);
                                }}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                            )}
                            {account.status === 'missing_data' && (
                              <Button variant="outline" size="sm">
                                Complete
                              </Button>
                            )}
                            {account.status === 'error' && (
                              <Button variant="outline" size="sm" className="text-red-600">
                                Fix Now
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {selectedRows.size > 0 && (
              <div className="mt-4 p-4 bg-primary/5 rounded-lg flex items-center justify-between">
                <span className="text-sm font-medium">
                  {selectedRows.size} account{selectedRows.size !== 1 ? 's' : ''} selected
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm">
                    Bulk Edit Selected
                  </Button>
                  <Button variant="outline" size="sm">
                    Invite Customer to Complete
                  </Button>
                </div>
              </div>
            )}

            <div className="mt-4 text-xs text-muted-foreground">
              <strong>Legend:</strong> ✓ Complete | ⚠ Missing Data | ❌ Error | Double-click cell to edit
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

